import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UserService } from '../../shared/services/user.service';
import { RestApiService } from '../../shared/services/restapi.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UtilityService } from '../../shared/services/utility.service';

@Component({
  selector: 'app-funstudios',
  templateUrl: './funstudios.component.html',
  styleUrls: ['./funstudios.component.scss']
})
export class FunstudiosComponent implements OnInit, OnDestroy {
  repName: string = '';
  repEmail: string = '';
  repPhone: string = '';
  webAlias: string = '';
  repCustomerId: any = 0; // Can be number or string (alphanumeric IDs)
  /** Sizzle video thumbnail (SIZZLEVIDEOCAPPAGETHUMBNAIL.png) */
  sizzleThumbnailUrl = 'assets/funstudios/SIZZLEVIDEOCAPPAGETHUMBNAIL.png';
  /** Vimeo embed URL for sizzle video */
  sizzleVideoUrl: SafeResourceUrl;
  /** Whether the user has clicked play (hides poster overlay) */
  videoPlaying = false;

  leadForm = {
    name: '',
    email: '',
    phone: ''
  };

  formSubmitted: boolean = false;
  isSubmitting: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private apiService: RestApiService,
    private notificationService: NotificationService,
    private utilityService: UtilityService
  ) {
    // Sanitize Vimeo URL for iframe (hide title, byline, and portrait) — same as Video Assets 2nd video
    this.sizzleVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://player.vimeo.com/video/1152018653?title=0&byline=0&portrait=0'
    );
  }

  onPlayClick(): void {
    this.videoPlaying = true;
  }

  ngOnInit(): void {
    this.utilityService.showSimplifiedheader(true);

    // Extract the webAlias from the full browser URL path (not Angular router URL)
    const fullPath = window.location.pathname;
    const pathSegments = fullPath.split('/').filter(segment => segment && !segment.includes('?'));

    // The webAlias should be the first segment if it exists and isn't 'funstudios'
    if (pathSegments.length > 0 && pathSegments[0] !== 'funstudios') {
      this.webAlias = pathSegments[0];
    }

    // Try to get rep info from user service enrollerInfo
    if (this.userService.userServiceModal?.enrollerInfo) {
      const enrollerInfo = this.userService.userServiceModal.enrollerInfo;
      this.repName = `${enrollerInfo.FirstName || ''} ${enrollerInfo.LastName || ''}`.trim();
      this.repCustomerId = enrollerInfo.CustomerId || 0;
      this.repEmail = enrollerInfo.EmailAddress || '';

      // If we didn't get a name, use the webAlias
      if (!this.repName && this.webAlias) {
        this.repName = this.webAlias;
      }
    } else if (this.webAlias) {
      // Fallback to webAlias if no enroller info
      this.repName = this.webAlias;
    }

    // If still no rep name, try to get it from localStorage
    if (!this.repName) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.FirstName || userData.LastName) {
        this.repName = `${userData.FirstName || ''} ${userData.LastName || ''}`.trim();
      } else if (userData.WebAlias) {
        this.repName = userData.WebAlias;
      }
      if (userData.CustomerId) {
        this.repCustomerId = userData.CustomerId;
      }
      if (userData.Email || userData.EmailAddress) {
        this.repEmail = userData.Email || userData.EmailAddress;
      }
      if (userData.Phone || userData.PhoneNumber || userData.MobilePhone) {
        this.repPhone = userData.Phone || userData.PhoneNumber || userData.MobilePhone;
      }
    }

    // Final fallback
    if (!this.repName) {
      this.repName = 'Your Rep';
    }
  }

  async onSubmit(): Promise<void> {
    // Validate form
    if (!this.leadForm.name || !this.leadForm.email || !this.leadForm.phone) {
      this.notificationService.error('Error', 'Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.leadForm.email)) {
      this.notificationService.error('Error', 'Please enter a valid email address');
      return;
    }

    this.isSubmitting = true;

    const leadData = {
      name: this.leadForm.name,
      email: this.leadForm.email,
      phone: this.leadForm.phone,
      repCustomerId: this.repCustomerId,
      repName: this.repName,
      repEmail: this.repEmail,
      repPhone: this.repPhone,
      webAlias: this.webAlias,
      source: 'funstudios',
      siteOrigin: window.location.origin,
      submittedDate: new Date().toISOString()
    };

    // Hardcoded production API URL for FunStudios backend
    const API_BASE_URL = 'https://tangerine-fun-funstudios.onrender.com';

    try {
      // Save lead to database
      const saveResponse = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(leadData)
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save lead');
      }

      // Fire emails in the background — don't block the form on email delivery
      fetch(`${API_BASE_URL}/api/send-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadData })
      });

    } catch (error) {
      console.error('Lead submission error:', error);
      this.notificationService.error('Error', 'Failed to submit lead. Please try again.');
      this.isSubmitting = false;
      return;
    }

    this.isSubmitting = false;
    this.formSubmitted = true;
    this.notificationService.success('Success', 'Your information has been submitted successfully!');

    // Redirect to thefunstudio (Coming Soon) page after 1 second
    setTimeout(() => {
      this.router.navigate(['/thefunstudio']);
    }, 1000);
  }

  ngOnDestroy(): void {
    this.utilityService.showSimplifiedheader(false);
  }
}
